import {
  DialogContent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  FormControlLabel,
  Checkbox,
  Button,
  styled,
  TableCell,
  tableCellClasses,
  Container,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { EstimationAmountState } from "../features/estimationAmount/estimationAmountSlice";
import { shallowEqual, useDispatch } from "react-redux";
import {
  updateEstimationFromList,
  selectEstimationAmountList,
  removeEstimationFromList,
} from "../features/estimationAmountList/estimationAmountListSlice";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { useAppSelector } from "../app/hooks";
import { useNavigate } from "react-router-dom";
import { addDays, format, isWeekend, startOfDay } from "date-fns";
import { formatISOToJST, toJSTISOString } from "../components/Utils/utils";
import {
  getDataDeliveryDateSetting,
  getReferenceDeliveryDaySetting,
} from "../features/systemSetting/systemSettingSlice";
import {
  selectFailedFiles,
  toggleFailedFileCheck,
} from "../features/failedFiles/failedFilesSlice";
import axiosConfig from "../axiosConfig";
import {
  update as updateCustomerInfo,
  selectCustomerInfo,
  customerInfoState,
} from "../features/customerInfo/customerInfoSlice";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#000",
    color: "#fff",
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    color: "#5589A7",
  },
}));
const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#F6F6F6",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EstimateAndDeliveryPlan: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const customerInfo: customerInfoState = useAppSelector(selectCustomerInfo);

  const [isDisable, setIsDisable] = useState(true);
  const [disabledDates, setDisabledDates] = useState<string[]>([]);
  // サーバー時刻とブラウザ時計の差（ミリ秒）。納期はサーバー時刻（JST）基準で表示する
  const [serverClockOffset, setServerClockOffset] = useState(0);

  const [totalAmount, setTotalAmount] = useState(0);
  const estimateAmountList: EstimationAmountState[] = useAppSelector(
    selectEstimationAmountList
  );
  const failedFiles = useAppSelector(selectFailedFiles);

  const [capacityStatus, setCapacityStatus] = useState<{
    max_processing_capacity: number;
    current_processing_count: number;
    remaining_capacity: number;
    is_available: boolean;
    per_option?: Array<{
      flex_setting_id: number;
      max_capacity: number;
      max_capacity_per_customer: number;
      current_count: number;
      remaining: number;
    }>;
  } | null>(null);
  useEffect(() => {
    const fetchCapacity = async () => {
      try {
        const response = await axiosConfig({
          method: "get",
          url: "/capacity/status",
        });
        setCapacityStatus(response.data.body);
      } catch (error) {
        console.error("Error fetching capacity status:", error);
      }
    };
    fetchCapacity();
  }, []);

  const checkedCountByFlexSettingId = useMemo(() => {
    const counts: Record<number, number> = {};
    estimateAmountList.forEach((item) => {
      if (item.checkBox && item.status === "uploaded" && item.flexSettingId != null) {
        counts[item.flexSettingId] = (counts[item.flexSettingId] || 0) + 1;
      }
    });
    return counts;
  }, [estimateAmountList]);
  const dataDeliveryDateSetting = useAppSelector(
    getDataDeliveryDateSetting,
    shallowEqual
  );
  const referenceDeliveryDaysSetting = useAppSelector(
    getReferenceDeliveryDaySetting
  );

  const baseAmount = useMemo(() => {
    return estimateAmountList.map((item) => item.estimationAmountBase);
  }, [estimateAmountList]);

  // Fetch api get non operating day
  useEffect(() => {
    let isHasData = false;
    estimateAmountList.forEach((item) => {
      if (item.status === "uploaded") isHasData = true;
    });
    if (!isHasData && failedFiles.length === 0) {
      navigate("/");
    }
    const fetchDisabledDates = async () => {
      try {
        const response = await axiosConfig({
          method: "get",
          // "https://6o556yrzjc.execute-api.ap-northeast-1.amazonaws.com/stage/non-operating-days"
          url: "/non-operating-days",
          params: { with_server_time: 1 },
        });
        const body = response.data.body;
        if (Array.isArray(body)) {
          // サーバー時刻を返さない旧 API の場合はブラウザの時計で表示する
          setDisabledDates(body);
        } else {
          setDisabledDates(body.non_operating_days);
          setServerClockOffset(
            new Date(body.server_time).getTime() - Date.now()
          );
        }
      } catch (error) {
        console.error("Error fetching disabled dates:", error);
      }
    };
    fetchDisabledDates();
  }, [navigate, estimateAmountList, failedFiles]);

  // Check is selected all - 見積選択 or 問い合わせ対象いずれかが1件以上で次へ可能
  useEffect(() => {
    const hasSelectedEstimate = estimateAmountList.some(
      (item) => item.checkBox && item.status === "uploaded"
    );
    const hasSelectedInquiry = failedFiles.some((f) => f.checkBox);
    setIsDisable(!hasSelectedEstimate && !hasSelectedInquiry);
  }, [estimateAmountList, failedFiles]);

  const handleChangeCheckbox = (
    row: number,
    deviation: number,
    priceRatio: number,
    flexSettingId?: number
  ) => {
    dispatch(
      updateEstimationFromList({
        index: row,
        deliveryDate: toJSTISOString(startOfDay(deliveryDatePlan(deviation))),
        estimationAmount: Math.round((baseAmount[row] ?? 0) * priceRatio),
        flexSettingId: flexSettingId ?? null,
      })
    );
  };

  const isChecked = (index: number, deviation: number) => {
    return (
      estimateAmountList[index].checkBox &&
      startOfDay(
        new Date(estimateAmountList[index]?.estimationDateTime)
      ).getTime() === startOfDay(deliveryDatePlan(deviation)).getTime()
    );
  };

  const handleSelectDeliveryPlan = () => {
    navigate("/input-customer-info");
  };

  // サーバー時刻の現在の JST 日付（ブラウザのローカル日付として表現）と時
  const getJSTNow = () => {
    const jst = new Date(Date.now() + serverClockOffset + 9 * 60 * 60 * 1000);
    return {
      date: new Date(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate()),
      hour: jst.getUTCHours(),
    };
  };

  // 保存される納期は BE（delivery_date.py）が同じ規則で算出する。ここは表示用
  const deliveryDatePlan = (deliveryDay: number) => {
    const workdays = referenceDeliveryDaysSetting + deliveryDay;

    const { date: today, hour: currentHour } = getJSTNow();

    let startDate = today;

    if (currentHour >= 12) {
      startDate = addDays(startDate, 1);
    }

    let currentDate = startDate;
    let validDays = 0;

    while (validDays < workdays) {
      const formattedDate = format(currentDate, "yyyy-MM-dd");

      const isWorkingDay =
        !isWeekend(currentDate) && !disabledDates.includes(formattedDate);

      if (isWorkingDay) {
        validDays++;

        if (validDays === workdays) {
          break;
        }
      }
      currentDate = addDays(currentDate, 1);
    }

    return currentDate;
  };

  // Calculate total amount of checked estimates
  useEffect(() => {
    const calculateTotal = () => {
      let total = 0;
      estimateAmountList.forEach((item) => {
        if (item.checkBox && item.status === "uploaded") {
          total += item.estimationAmount || 0;
        }
      });
      setTotalAmount(total);
    };
    calculateTotal();
  }, [estimateAmountList]);

  // NEW: Handle delete estimation item
  const handleDeleteEstimation = (index: number) => {
    if (window.confirm("この見積もりを削除しますか？")) {
      dispatch(removeEstimationFromList(index));
    }
  };

  return (
    <>
      <Header pageTitle="納期の選択" />
      <Container
        style={{ marginTop: "2%", marginBottom: "150px", minWidth: "90vw" }}
      >
        <DialogContent sx={{ paddingTop: 0 }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">ファイル名</StyledTableCell>
                  {dataDeliveryDateSetting.map((item) => {
                    return (
                      <StyledTableCell align="center" key={item.id}>
                        <div style={{ fontSize: "20px", fontWeight: "bold", lineHeight: 1.2 }}>
                          {referenceDeliveryDaysSetting + item.deviation}営業日
                        </div>
                        <p style={{ fontWeight: "normal", margin: "4px 0 0 0" }}>
                          本日{getJSTNow().date.getDate()}日中のご発注で
                        </p>
                        <p style={{ fontWeight: "normal", margin: 0 }}>
                          {formatISOToJST(
                            deliveryDatePlan(item.deviation).toISOString()
                          )}ご納品
                        </p>
                      </StyledTableCell>
                    );
                  })}
                  <StyledTableCell align="center">操作</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimateAmountList.map(
                  (row, indexRow) =>
                    row.status === "uploaded" && (
                      <StyledTableRow key={indexRow}>
                        <StyledTableCell
                          align="center"
                          component="th"
                          scope="row"
                          size="small"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "12px",
                              padding: "8px",
                            }}
                          >
                            <span style={{ flex: 1, textAlign: "left" }}>
                              {row.estimationFileName}
                            </span>
                            {row.urlImage && (
                              <div
                                style={{
                                  flexShrink: 0,
                                  width: "80px",
                                  height: "60px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  order: 1,
                                  position: "relative",
                                }}
                              >
                                <img
                                  src={row.urlImage}
                                  alt="Preview"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    border: "1px solid #e0e0e0",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    const hoverImg =
                                      document.createElement("img");
                                    hoverImg.src = row.urlImage ?? "";
                                    hoverImg.style.cssText = `
                                        position: fixed;
                                        top: 50%;
                                        left: 50%;
                                        transform: translate(-50%, -50%);
                                        max-width: 80vw;
                                        max-height: 80vh;
                                        z-index: 9999;
                                        border-radius: 8px;
                                        box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                                        border: 2px solid #fff;
                                        background: #fff;
                                        object-fit: contain;
                                        pointer-events: none;
                                      `;
                                    hoverImg.id = "hover-image-preview";
                                    document.body.appendChild(hoverImg);

                                    // Add overlay
                                    const overlay =
                                      document.createElement("div");
                                    overlay.style.cssText = `
                                        position: fixed;
                                        top: 0;
                                        left: 0;
                                        width: 100%;
                                        height: 100%;
                                        background: rgba(0,0,0,0.5);
                                        z-index: 9998;
                                        pointer-events: none;
                                      `;
                                    overlay.id = "hover-overlay";
                                    document.body.appendChild(overlay);
                                  }}
                                  onMouseLeave={() => {
                                    const hoverImg = document.getElementById(
                                      "hover-image-preview"
                                    );
                                    const overlay =
                                      document.getElementById("hover-overlay");
                                    if (hoverImg) hoverImg.remove();
                                    if (overlay) overlay.remove();
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </StyledTableCell>
                        {dataDeliveryDateSetting.map((item, indexCheckbox) => {
                          const optionCapacity = capacityStatus?.per_option?.find(
                            (o) => o.flex_setting_id === item.id
                          );
                          const checkedInColumn = checkedCountByFlexSettingId[item.id] || 0;
                          const dynamicRemaining = optionCapacity
                            ? optionCapacity.remaining - checkedInColumn
                            : Infinity;
                          const customerRemaining = optionCapacity && optionCapacity.max_capacity_per_customer > 0
                            ? optionCapacity.max_capacity_per_customer - checkedInColumn
                            : Infinity;
                          const isOptionFull = optionCapacity
                            ? (optionCapacity.max_capacity > 0 && dynamicRemaining <= 0) ||
                              (optionCapacity.max_capacity_per_customer > 0 && customerRemaining <= 0)
                            : false;
                          const isDisabledByCapacity =
                            !isChecked(indexRow, item.deviation) && isOptionFull;
                          return (
                            <StyledTableCell
                              key={indexCheckbox}
                              align="center"
                              size="small"
                            >
                              <FormControlLabel
                                label={`${Math.round(
                                  (baseAmount[indexRow] ?? 0) * item.priceRatio
                                )}円`}
                                control={
                                  <Checkbox
                                    id={`${indexCheckbox}`}
                                    checked={isChecked(indexRow, item.deviation)}
                                    disabled={isDisabledByCapacity}
                                    onChange={() =>
                                      handleChangeCheckbox(
                                        indexRow,
                                        item.deviation,
                                        item.priceRatio,
                                        item.id
                                      )
                                    }
                                  />
                                }
                              />
                            </StyledTableCell>
                          );
                        })}
                        {/* NEW: Delete button column */}
                        <StyledTableCell align="center" size="small">
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDeleteEstimation(indexRow)}
                            sx={{
                              minWidth: "auto",
                              padding: "4px 8px",
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            削除
                          </Button>
                        </StyledTableCell>
                      </StyledTableRow>
                    )
                )}
                {failedFiles.map((failed) => (
                  <StyledTableRow key={`failed-${failed.s3FileName}`}>
                    <StyledTableCell align="center" component="th" scope="row" size="small">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "8px",
                        }}
                      >
                        <span style={{ flex: 1, textAlign: "left" }}>
                          {failed.fileName}
                        </span>
                        {failed.urlImage && (
                          <div
                            style={{
                              flexShrink: 0,
                              width: "80px",
                              height: "60px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={failed.urlImage}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                borderRadius: "6px",
                                objectFit: "cover",
                                border: "1px solid #e0e0e0",
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      colSpan={dataDeliveryDateSetting.length}
                      sx={{ backgroundColor: "#fffbe6" }}
                    >
                      <FormControlLabel
                        label="詳細確認が必要なため、お見積できませんでした。お手数ですが、お見積フォームより別途お見積をご依頼ください。"
                        control={
                          <Checkbox
                            checked={failed.checkBox}
                            onChange={() =>
                              dispatch(toggleFailedFileCheck(failed.s3FileName))
                            }
                          />
                        }
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center" size="small" />
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        {/* <DialogActions>
          <Button
            variant="contained"
            sx={{ paddingX: "40px", margin: "0 auto 15px auto" }}
            onClick={handleSelectDeliveryPlan}
            disabled={isDisable}
          >
            次へ
          </Button>
        </DialogActions> */}
        {/* 備考欄（任意） - 設計05: 見積書PDF・注文詳細に反映される */}
        <Box sx={{ mt: 3, mb: "160px", px: 1 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            備考（任意）
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="見積書に記載する備考があればご入力ください"
            value={customerInfo.remarks || ""}
            onChange={(e) =>
              dispatch(updateCustomerInfo({ remarks: e.target.value }))
            }
          />
        </Box>
      </Container>
      {/* Floating Total Amount Section - Reduced size */}
      <div
        style={{
          position: "fixed",
          bottom: "60px", // Above footer
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          borderTop: "1px solid #e0e0e0",
          zIndex: 1000,
          padding: "12px 24px",
        }}
      >
        <Container style={{ minWidth: "90vw" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                合計見積金額:
              </span>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#1976d2",
                  background: "#f5f5f5",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "2px solid #1976d2",
                }}
              >
                {totalAmount.toLocaleString("ja-JP")}円
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "4px",
              }}
            >
              {isDisable && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "#f44336",
                  }}
                >
                  ※ 少なくとも1つの見積もりを選択してください
                </span>
              )}
              <Button
                variant="contained"
                size="medium"
                sx={{
                  paddingX: "32px",
                  paddingY: "8px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  minWidth: "100px",
                }}
                onClick={handleSelectDeliveryPlan}
                disabled={isDisable}
              >
                次へ
              </Button>
            </div>
          </div>
        </Container>
      </div>
      <Footer />
    </>
  );
};
export default EstimateAndDeliveryPlan;
